/**
 * First-person movement, look and collision.
 *
 * The controller owns `PlayerState` and is the first system to run each frame,
 * so everything downstream reads a settled position. It composes four small
 * pieces rather than doing the work inline:
 *
 *   collision.ts  circle-vs-world sliding, the part that has to be flawless
 *   camera.ts     head bob, sway, roll, crouch blend, landing dip
 *   footsteps.ts  step cadence, surface and the noise entities hear
 *   flashlight.ts ownership, charge and the toggle
 *
 * Feel notes. Velocity is accelerated toward a target rather than assigned, so
 * a step off is a push rather than a snap, and airborne control is a fraction
 * of ground control so a jump commits. Speed is measured from the distance
 * actually covered rather than from the commanded velocity, which means a
 * player grinding into a wall makes no footsteps, no bob and no noise — the
 * cheapest way to be quiet should not be an exploit, and walking into a corner
 * should not summon a hound.
 */

import { TUNING } from '../core/config';
import { clamp, damp, TAU } from '../core/math';
import type { PlayerController } from '../core/systems';
import { Cell, type EventBus, type GameContext, type GeneratedLevel, type PlayerState, type Vec2 } from '../core/types';

import { makeHeadBob, type HeadBob } from './camera';
import { makeCollisionWorld, type CollisionWorld } from './collision';
import { makeFlashlight, type Flashlight } from './flashlight';
import { makeFootsteps, type Footsteps } from './footsteps';

/** Free space wanted above the eyes before the player may stand up, metres. */
const HEAD_CLEARANCE = 0.18;

/** Speed below which a crouched player counts as holding still, m/s. */
const HIDE_SPEED = 0.3;

/** Impact speed above which landing is loud enough to be a footstep, m/s. */
const LAND_STEP_SPEED = 1.2;

/** Seconds of reduced control after being hit. */
const STAGGER_TIME = 0.35;

/** Fraction of normal acceleration retained while staggered. */
const STAGGER_CONTROL = 0.28;

/** Absolute ceiling on horizontal speed, so an impulse can never launch the player. */
const MAX_SPEED = 14;

/** How fast momentum bleeds off while frozen, 1/seconds. */
const FROZEN_DECAY = 12;

class Controller implements PlayerController {
  readonly state: PlayerState = {
    pos: { x: 0, z: 0 },
    eyeHeight: TUNING.player.eyeHeight,
    yaw: 0,
    pitch: 0,
    vel: { x: 0, z: 0 },
    speed: 0,
    crouching: false,
    sprinting: false,
    hidden: false,
    flashlightOn: false,
    flashlightCharge: 0,
    frozen: false,
    noise: 0,
    speedScale: 1,
  };

  private level: GeneratedLevel | null = null;
  private readonly collision: CollisionWorld = makeCollisionWorld();
  private readonly bob: HeadBob = makeHeadBob();
  private readonly steps: Footsteps;
  private readonly light: Flashlight;

  /** Feet above the floor plane. Folded into `eyeHeight`; the grid stays 2D. */
  private height = 0;
  private velY = 0;
  private grounded = true;
  private stagger = 0;
  private stillTime = 0;

  /** Reused by `getEyePosition` so the render path allocates nothing. */
  private readonly eye = { x: 0, y: 0, z: 0 };

  private readonly offStart: () => void;

  constructor(private readonly events: EventBus) {
    this.steps = makeFootsteps(events);
    this.light = makeFlashlight(events);
    // A new run must not inherit the last run's flashlight or exhaustion.
    this.offStart = events.on('game:start', () => this.resetRun());
  }

  setLevel(level: GeneratedLevel): void {
    this.level = level;
    this.collision.setLevel(level);
    this.steps.setLevel(level);
    this.light.setDrains(!level.spec.freeFlashlight);
    this.teleport(level.spawn, level.spawnYaw);
  }

  update(ctx: GameContext): void {
    const s = this.state;
    const { dt, input } = ctx;
    if (dt <= 0) return;

    // The beam burns whether or not the player is allowed to act.
    this.light.update(dt, s);

    if (s.frozen) {
      s.vel.x = damp(s.vel.x, 0, FROZEN_DECAY, dt);
      s.vel.z = damp(s.vel.z, 0, FROZEN_DECAY, dt);
      s.speed = 0;
      s.noise = 0;
      this.bob.update(dt, 0, s.yaw, 0, false, s.crouching, this.grounded);
      s.eyeHeight = this.bob.baseEye + this.height + this.bob.offsetY;
      return;
    }

    const P = TUNING.player;

    // The camera negates this on its way into three.js (`renderYaw`), so adding
    // here is what makes a rightward mouse move turn right on screen.
    s.yaw += input.lookX;
    // Keep yaw bounded so it never loses precision over a long run.
    if (s.yaw > Math.PI) s.yaw -= TAU;
    else if (s.yaw < -Math.PI) s.yaw += TAU;
    s.pitch = clamp(s.pitch - input.lookY, -P.maxPitch, P.maxPitch);

    if (input.pressed('flashlight')) this.light.toggle(s);

    this.updateStance(ctx);
    const sprinting = this.decideSprint(ctx);

    /* Horizontal acceleration toward the wished-for velocity. */
    const moveX = clamp(input.moveX, -1, 1);
    const moveZ = clamp(input.moveZ, -1, 1);
    const sinY = Math.sin(s.yaw);
    const cosY = Math.cos(s.yaw);
    let wishX = sinY * moveZ + cosY * moveX;
    let wishZ = -cosY * moveZ + sinY * moveX;
    const wishLen = Math.hypot(wishX, wishZ);
    if (wishLen > 1e-4) {
      wishX /= wishLen;
      wishZ /= wishLen;
    }

    const stanceSpeed = s.crouching ? P.crouchSpeed : sprinting ? P.sprintSpeed : P.walkSpeed;
    const target =
      stanceSpeed *
      Math.max(0, s.speedScale) *
      (this.steps.inWater ? P.waterSpeedScale : 1) *
      (moveZ < 0 ? P.backwardScale : 1);

    const control = (this.grounded ? 1 : P.airControl) * (this.stagger > 0 ? STAGGER_CONTROL : 1);
    // Braking is a foot on the floor. Airborne there is only a whisper of drag,
    // so a jump carries its momentum instead of stalling in mid-air.
    const rate =
      wishLen > 1e-4 ? P.accel * control : P.decel * (this.grounded ? (this.stagger > 0 ? STAGGER_CONTROL : 1) : P.airDrag);
    const toX = wishX * target - s.vel.x;
    const toZ = wishZ * target - s.vel.z;
    const toLen = Math.hypot(toX, toZ);
    const maxStep = rate * dt;
    if (toLen > maxStep && toLen > 1e-6) {
      s.vel.x += (toX / toLen) * maxStep;
      s.vel.z += (toZ / toLen) * maxStep;
    } else {
      s.vel.x += toX;
      s.vel.z += toZ;
    }
    this.clampSpeed();

    this.updateVertical(ctx);

    const fromX = s.pos.x;
    const fromZ = s.pos.z;
    this.collision.move(s.pos, s.vel, s.vel.x * dt, s.vel.z * dt, P.radius);
    const travelled = Math.hypot(s.pos.x - fromX, s.pos.z - fromZ);
    s.speed = travelled / dt;

    if (this.steps.update(dt, s.pos, travelled, s.speed, s.crouching, sprinting)) {
      this.bob.syncToStep();
    }
    s.noise = this.steps.noise;

    const resting = s.crouching && s.speed < HIDE_SPEED;

    if (s.crouching && s.speed < HIDE_SPEED && this.grounded) this.stillTime += dt;
    else this.stillTime = 0;
    s.hidden = this.stillTime >= P.hideDelay;

    if (this.stagger > 0) this.stagger = Math.max(0, this.stagger - dt);

    this.bob.update(dt, s.speed, s.yaw, moveX, sprinting, s.crouching, this.grounded);
    s.eyeHeight = this.bob.baseEye + this.height + this.bob.offsetY;
  }

  getEyePosition(): { x: number; y: number; z: number } {
    // Shared object: read it, do not keep it.
    this.eye.x = this.state.pos.x + this.bob.offsetX;
    this.eye.y = this.state.eyeHeight;
    this.eye.z = this.state.pos.z + this.bob.offsetZ;
    return this.eye;
  }

  getRoll(): number {
    return this.bob.roll;
  }

  /**
   * The `headBob` render setting. The renderer can only drop the lateral sway
   * and the roll — the vertical arc is baked into `state.eyeHeight` here, so
   * turning it off has to happen on this side or the setting does nothing to
   * the one axis players turn it off for.
   */
  setHeadBob(on: boolean): void {
    this.bob.setEnabled(on);
  }

  /**
   * The body was moved by something that is not this controller.
   *
   * Only an immersive session does that: the player physically walks, the head
   * goes with them and `render/xr.ts` drags `PlayerState.pos` along underneath
   * it. `s.speed` is the *controller's* travel and is zero throughout, so
   * without this the hide latch — "crouched and holding still" — would hold
   * while somebody duck-walked across the room, which is neither still nor
   * something an entity should be unable to notice.
   *
   * The same threshold the controller applies to its own movement, so the two
   * kinds of walking are worth the same. Never called on a flat machine.
   *
   * `distance` is the ground covered, and it is what makes the movement audible.
   * The step accumulator is driven by distance rather than by time — that is the
   * whole of `player/footsteps.ts` — so handing it the metres is enough to get
   * footfalls at the right cadence: measured, 155 footfalls over two minutes of
   * teleport travel against 155 over the same two minutes walked, and 74 either
   * way crouched. They are priced at whatever `PlayerState.noise` currently
   * says, which is the level the mover has already published, so a crouched hop
   * stays under the hearing threshold and a sprint-dash does not — exactly as
   * the same journey on foot does. Omitted, the motion is silent, which is what
   * a recentre or a level load wants.
   */
  externalMotion(speed: number, distance = 0): void {
    if (speed >= HIDE_SPEED) this.stillTime = 0;
    if (distance > 0) this.steps.external(this.state.pos, distance, this.state.noise, speed);
  }

  /** See `PlayerController.restoreFlashlight`. */
  restoreFlashlight(charge: number, on: boolean): void {
    this.light.restore(this.state, charge, on);
  }

  teleport(pos: Vec2, yaw?: number): void {
    const s = this.state;
    s.pos.x = pos.x;
    s.pos.z = pos.z;
    s.vel.x = 0;
    s.vel.z = 0;
    s.speed = 0;
    s.noise = 0;
    s.hidden = false;
    if (yaw !== undefined) s.yaw = yaw;

    this.velY = 0;
    this.height = 0;
    this.grounded = true;
    this.stagger = 0;
    this.stillTime = 0;

    // A spawn point can sit close enough to a wall to overlap the body, and a
    // prop placed after the spawn was chosen can be standing on top of it.
    this.collision.findFree(s.pos, TUNING.player.radius);
    this.bob.reset(s.crouching);
    this.steps.reset();
    s.eyeHeight = this.bob.baseEye;
  }

  applyImpulse(dir: Vec2, strength: number): void {
    if (strength <= 0) return;
    const l = Math.hypot(dir.x, dir.z);
    if (l < 1e-6) return;
    const s = this.state;
    s.vel.x += (dir.x / l) * strength;
    s.vel.z += (dir.z / l) * strength;
    this.clampSpeed();
    // Briefly hand control to the knockback, or acceleration erases it instantly.
    this.stagger = STAGGER_TIME;
  }

  setSpeedScale(scale: number): void {
    this.state.speedScale = clamp(scale, 0, 4);
  }

  dispose(): void {
    this.offStart();
    this.light.dispose();
  }

  /* ---------------------------------------------------------------- *
   * Internals
   * ---------------------------------------------------------------- */

  /** Crouch, with a headroom test so the player cannot stand up into geometry. */
  private updateStance(ctx: GameContext): void {
    const s = this.state;
    const want = ctx.input.isDown('crouch');
    const crouching = want || (s.crouching && !this.canStand());
    if (crouching !== s.crouching) {
      s.crouching = crouching;
      this.events.emit('player:crouch', { on: crouching });
    }
  }

  /** Sprint is forward-only and standing-only. It costs nothing to keep running. */
  private decideSprint(ctx: GameContext): boolean {
    const s = this.state;
    const wants = ctx.input.isDown('sprint') && ctx.input.moveZ > 0 && !s.crouching;
    const sprinting = wants;
    if (sprinting !== s.sprinting) {
      s.sprinting = sprinting;
      this.events.emit('player:sprint', { on: sprinting });
    }
    return sprinting;
  }

  private updateVertical(ctx: GameContext): void {
    const P = TUNING.player;
    const s = this.state;
    const dt = ctx.dt;

    if (this.grounded && ctx.input.pressed('jump') && !s.crouching) {
      this.velY = P.jumpSpeed;
      this.grounded = false;
    }
    if (this.grounded) return;

    this.velY -= P.gravity * dt;
    this.height += this.velY * dt;

    const maxHeight = this.ceilingClearance();
    if (this.height > maxHeight) {
      this.height = maxHeight;
      if (this.velY > 0) this.velY = 0;
    }

    if (this.height <= 0) {
      const impact = -this.velY;
      this.height = 0;
      this.velY = 0;
      this.grounded = true;
      this.bob.land(impact);
      if (impact > LAND_STEP_SPEED) this.steps.land(s.pos, impact);
    }
  }

  /**
   * Free height above the feet here. Walls are full height, so today this only
   * ever differs under an open ceiling — but routing standing and jumping
   * through it means a generator that carves a crawlspace cannot trap anyone.
   *
   * Props are deliberately not consulted. A footprint is a 2D box with no base
   * elevation, so a soffit crossing the room at 2.6 m would read here as a
   * full-height obstruction and stop a crouched player standing up in the
   * middle of an empty room. Anything overhead is authored without a footprint
   * and is therefore not in the collision index at all; see `PropSpot`.
   */
  private headroom(): number {
    const level = this.level;
    if (!level) return Infinity;
    const grid = level.grid;
    const cell = grid.at(Math.floor(this.state.pos.x / grid.cellSize), Math.floor(this.state.pos.z / grid.cellSize));
    if (cell === Cell.Open) return Infinity;
    return level.spec.ceilingHeight;
  }

  private canStand(): boolean {
    return this.headroom() >= TUNING.player.eyeHeight + HEAD_CLEARANCE;
  }

  /** Highest the feet may reach before the head meets the ceiling. */
  private ceilingClearance(): number {
    const room = this.headroom();
    if (!Number.isFinite(room)) return Infinity;
    return Math.max(0, room - (this.bob.baseEye + HEAD_CLEARANCE));
  }

  private clampSpeed(): void {
    const v = this.state.vel;
    const l = Math.hypot(v.x, v.z);
    if (l > MAX_SPEED) {
      v.x = (v.x / l) * MAX_SPEED;
      v.z = (v.z / l) * MAX_SPEED;
    }
  }

  private resetRun(): void {
    const s = this.state;
    s.frozen = false;
    s.crouching = false;
    s.sprinting = false;
    s.hidden = false;
    s.speedScale = 1;
    s.pitch = 0;
    this.light.reset(s);
    this.bob.reset(false);
    this.steps.reset();
    this.stillTime = 0;
    this.stagger = 0;
    this.height = 0;
    this.velY = 0;
    this.grounded = true;
    s.eyeHeight = this.bob.baseEye;
  }
}

export function makePlayerController(events: EventBus): PlayerController {
  return new Controller(events);
}
